import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormikContext } from "formik";
import {
  Box,
  Button,
  Center,
  Grid,
  GridItem,
  Text,
  Icon,
  RadioGroup,
  Radio,
  Stack,
  useColorMode,
  HStack,
} from "@chakra-ui/react";
import { AiFillBank } from "react-icons/ai";
import { IoFlashSharp } from "react-icons/io5";
import { Money } from "components/Money";
import { GenericBankLogo } from "components/Logos";

const getDepositDurationStr = (minDays, maxDays, formatMessage) => {
  let str = "";
  if (minDays === maxDays) {
    if (minDays === 0) {
      str = formatMessage({
        defaultMessage: "Same day",
        description: "Describes time",
      });
    } else if (minDays === 1) {
      str = formatMessage({ defaultMessage: "1 day", description: "Describes time" });
    } else {
      str = formatMessage(
        {
          defaultMessage: "{minDays} days",
          description: "Example: 3 days",
        },
        { minDays }
      );
    }
  } else {
    str = formatMessage(
      {
        defaultMessage: "{minDays}-{maxDays} days",
        description: "Describes time range. Example: 3-5 days",
      },
      {
        minDays,
        maxDays,
      }
    );
  }

  return str;
};

const BankLogoCard = ({ bankName, description }) => {
  return (
    <Stack pt="5" pb="5" pl="4" pr="4" borderRadius="8" bgColor="integrator.50">
      <HStack>
        <GenericBankLogo />
        <Text color="primaryColor" fontWeight="bold">
          {bankName}
        </Text>
      </HStack>
      <Text>{description}</Text>
    </Stack>
  );
};

const DepositType = ({ depositType, selected, onClick, hideRadio }) => {
  const { formatMessage } = useIntl();
  const { kind, fee_percentage, fee_amount, total_amount, estimated_duration } = depositType;
  const { minimum_business_days, maximum_business_days } = estimated_duration;
  const depositDuration = getDepositDurationStr(
    minimum_business_days,
    maximum_business_days,
    formatMessage
  );
  const depositKindToConsts = {
    ACH: {
      displayText: formatMessage({
        defaultMessage: "Transfer from your bank (ACH)",
        description:
          "Describes a type of money transfer. ACH is a proper value that does not have to be translated. It is a type of money transfer.",
      }),
      icon: AiFillBank,
    },
    Wire: {
      displayText: formatMessage({
        defaultMessage: "Wire transfer",
        description: "Describes a type of money transfer",
      }),
      icon: IoFlashSharp,
    },
  };
  const depositConst = depositKindToConsts[kind];
  const { colorMode } = useColorMode();
  const invertedColorMode =
    colorMode === "dark" && selected
      ? "light"
      : colorMode === "dark" && !selected
      ? "dark"
      : colorMode; // light color mode
  const iconColor =
    colorMode === "dark" && selected
      ? "integrator.500"
      : colorMode === "dark" && !selected
      ? null
      : "integrator.500"; // light color mode

  return (
    <Grid
      onClick={onClick}
      role={kind}
      gridTemplateColumns="auto 1fr"
      columnGap="10"
      rowGap="5"
      templateAreas={`"radioBtn title"
                  ". text"`}
      pt="10"
      pb="10"
      pl="5"
      pr="5"
      bgColor={selected && "integrator.50"}
      border="1px solid rgba(0, 0, 0, 0.04)"
      borderColor={selected && "integrator.300"}
      boxShadow="base"
      borderRadius="8"
      cursor="pointer"
    >
      <Center>{hideRadio ? null : <Radio value={kind} />}</Center>
      <Grid gridTemplateColumns="auto 1fr" gridGap="3">
        <Box color={iconColor}>
          <Icon as={depositConst.icon} boxSize="12" />
        </Box>
        <Box data-theme={invertedColorMode}>
          <Text fontWeight="bold" color="primaryText">
            {depositConst.displayText}
          </Text>
          <Text color="primaryText">
            {`${depositDuration} | ${formatMessage(
              { defaultMessage: "{percentage}% fee", description: "Example: 95% fee" },
              {
                percentage: fee_percentage,
              }
            )}`}
          </Text>
        </Box>
      </Grid>
      <GridItem area="text" data-theme={invertedColorMode}>
        <Text color="primaryText">
          <FormattedMessage
            defaultMessage="Your deposit will be assessed a <fee>$[amount]</fee> <bold>fee</bold>, for a total of <total>$[amount]</total>"
            description="The markup tags (</>) can be ignored by translators. Square brackets [] are placeholders for money amounts."
            values={{
              bold: (msg) => (
                <Text as="span" fontWeight="bold" color="primaryText">
                  {msg}
                </Text>
              ),
              fee: () => (
                <Text as="span" fontWeight="bold" color="primaryText">
                  $<Money currency="USD" amount={fee_amount} showSymbol={false} />
                </Text>
              ),
              total: () => (
                <Text as="span" fontWeight="bold" color="primaryText">
                  $<Money currency="USD" amount={total_amount} showSymbol={false} />
                </Text>
              ),
            }}
          />
        </Text>
      </GridItem>
    </Grid>
  );
};

const SelectDepositType = ({ depositOptions, onSubmit }) => {
  const { setFieldValue, values } = useFormikContext();

  const handleChange = (deposit) => {
    setFieldValue("depositType", deposit.kind);
    setFieldValue("requestAmountFee", deposit.fee_amount);
  };

  const handleSubmit = () => {
    onSubmit(values);
  };

  const bankName = values.paymentMethod.provider_data?.bank_name;
  const description = values.paymentMethod.display_string;

  return (
    <Box>
      <Box mb="6">
        <BankLogoCard bankName={bankName} description={description} />
      </Box>
      <Text fontWeight="bold" color="primaryText" mb="3">
        <FormattedMessage
          defaultMessage="Please select an option below"
          description="Description of action"
        />
      </Text>
      <Stack spacing={10}>
        <RadioGroup onChange={handleChange} value={values.depositType}>
          <Stack spacing={4}>
            {depositOptions.map((depositType) => (
              <DepositType
                key={depositType.kind}
                depositType={depositType}
                selected={values.depositType === depositType.kind}
                onClick={() => handleChange(depositType)}
                hideRadio={depositOptions.length === 1}
              />
            ))}
          </Stack>
        </RadioGroup>
        <Button width="full" isDisabled={!values.depositType} onClick={handleSubmit} type="submit">
          <FormattedMessage defaultMessage="Load funds" description="Button label" />
        </Button>
      </Stack>
    </Box>
  );
};

export default SelectDepositType;
